/**
 * Canonical CheckResult factory per Amendment v3.1 §A1.
 *
 * Every check's engine layer MUST emit a result built by this factory. Display
 * layer (PDF templates, dashboard components, DCMA renderer) consumes the
 * result verbatim per §A2 — render-only, never recalculate.
 *
 * Aux fields (top-level passthroughs beyond §A1) per check:
 *   #3  dcma3, dcma4, dcma5, dcma6, ss_count, ff_count, sf_count,
 *       leads_count, positive_lags_count, excessive_lags_count
 *   #19 buckets (histogram bins), analyzable_float_days
 *   #20 cp_analysis (continuous, breaks[], components[])
 *   #21 cp_activity_count, analyzable_activity_count (v3.17 §1 redefinition)
 *   #22 cp_path_count, parallel_path_count
 *   #29 wbs_structure (depths[], leaf_count)
 *   #30 wbs_node_count, empty_node_paths[]
 *   #36 data_quality_warning (when project has no TASKRSRC rows)
 *
 * Display layer may READ aux fields but MUST NOT recalculate from them.
 * Any new derived value belongs in the engine layer, exposed as a new aux key
 * — never re-computed downstream.
 *
 * @module checks/checkResult
 */

export const STATUS_THRESHOLD = Object.freeze({
  PASS: 'PASS',
  EXCEEDED: 'EXCEEDED',
  REPORT_ONLY: 'REPORT_ONLY',
  HIDDEN: 'HIDDEN',
});

export const STATUS_SEVERITY = Object.freeze({
  CRITICAL: 'CRITICAL',
  WARNING: 'WARNING',
  LOW: 'LOW',
  NONE: 'NONE',
});

export const DIRECTION = Object.freeze({
  LOWER_IS_BETTER: 'lower_is_better',
  HIGHER_IS_BETTER: 'higher_is_better',
  RANGE: 'range',
  REPORT_ONLY: 'report_only',
  BINARY: 'binary',
});

export const FINDING_TYPE = Object.freeze({
  ACTIVITY_LEVEL: 'activity_level',
  SCHEDULE_LEVEL: 'schedule_level',
  REPORT_ONLY: 'report_only',
});

/**
 * Threshold display strings — Amendment v3.1 §3 lock table. Engine emits
 * verbatim; display renders verbatim. Drift between this table and §3 is a
 * regression and must be caught by `scripts/smoke-canonical-shape.mjs`.
 */
export const THRESHOLD_LABELS = Object.freeze({
  1: { label: '<5%', direction: DIRECTION.LOWER_IS_BETTER },
  2: { label: '<5%', direction: DIRECTION.LOWER_IS_BETTER },
  3: { label: 'Clean FS relationships ≥ 80% (DCMA: 90% pure FS)', direction: DIRECTION.HIGHER_IS_BETTER },
  4: { label: '0 dangling allowed', direction: DIRECTION.LOWER_IS_BETTER },
  5: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  6: { label: '0 circular loops allowed', direction: DIRECTION.LOWER_IS_BETTER },
  7: { label: '<5%', direction: DIRECTION.LOWER_IS_BETTER },
  8: { label: '<15%', direction: DIRECTION.LOWER_IS_BETTER },
  9: { label: '<20%', direction: DIRECTION.LOWER_IS_BETTER },
  10: { label: '0 invalid constraints allowed', direction: DIRECTION.LOWER_IS_BETTER },
  11: { label: '0 conflicts allowed', direction: DIRECTION.LOWER_IS_BETTER },
  12: { label: '0 zero-duration non-milestones allowed', direction: DIRECTION.LOWER_IS_BETTER },
  13: { label: '<5% (max duration: 44d)', direction: DIRECTION.LOWER_IS_BETTER },
  14: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  15: { label: '0 LOE on critical path allowed', direction: DIRECTION.LOWER_IS_BETTER },
  16: { label: '0 negative-float activities allowed', direction: DIRECTION.LOWER_IS_BETTER },
  17: { label: 'Near-critical activities (1-10d float)', direction: DIRECTION.REPORT_ONLY },
  18: { label: '<5% (>44d float)', direction: DIRECTION.LOWER_IS_BETTER },
  19: { label: 'Distribution view', direction: DIRECTION.REPORT_ONLY },
  20: { label: 'CP must be continuous start to finish', direction: DIRECTION.BINARY },
  21: { label: 'Critical activities: 5%–20% of analysable activities', direction: DIRECTION.RANGE },
  22: { label: '<1% multiple parallel paths', direction: DIRECTION.LOWER_IS_BETTER },
  23: { label: '0 constraint-driven CP activities allowed', direction: DIRECTION.LOWER_IS_BETTER },
  24: { label: '0 missing/default calendar assignments', direction: DIRECTION.LOWER_IS_BETTER },
  25: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  26: { label: '0 in-progress activities behind data date', direction: DIRECTION.LOWER_IS_BETTER },
  27: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  28: { label: '0 orphan activities allowed', direction: DIRECTION.LOWER_IS_BETTER },
  29: { label: 'WBS depth: 2–6 levels (inclusive of root)', direction: DIRECTION.RANGE },
  30: { label: '0 empty WBS nodes allowed', direction: DIRECTION.LOWER_IS_BETTER },
  31: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  32: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  33: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  34: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  35: { label: 'Report count', direction: DIRECTION.REPORT_ONLY },
  36: { label: '100% of activities resource/cost loaded (DCMA standard)', direction: DIRECTION.HIGHER_IS_BETTER },
  37: { label: '0 corruption ghosts allowed; unused calendars informational', direction: DIRECTION.LOWER_IS_BETTER },
});

/**
 * Denominator semantics per check — short noun-phrase describing what the
 * denominator counts. Appended to the rendered "N of M" string so readers
 * never see a bare "0 of 1" with no context (PDF-POLISH-2 D22).
 *
 * Check #20 is BINARY — its result line is replaced entirely via
 * `result_display_override` (PASS: "Continuous — …"; FAIL: "BROKEN — N segment(s) detected"),
 * so its `denominator_label` is left null and consumers must branch on the
 * override first. All non-REPORT_ONLY checks must emit a non-null label;
 * REPORT_ONLY checks populate the label for shape-completeness even though
 * their result line is suppressed.
 */
export const DENOMINATOR_LABELS = Object.freeze({
  1:  'analysable activities',
  2:  'analysable activities',
  3:  'relationships',
  4:  'analysable activities',
  5:  'analysable activities',
  6:  'analysable activities',
  7:  'analysable activities',
  8:  'analysable activities',
  9:  'analysable activities',
  10: 'analysable activities',
  11: 'analysable activities',
  12: 'analysable activities',
  13: 'analysable activities',
  14: 'analysable activities',
  15: 'LOE activities',
  16: 'analysable activities',
  17: 'analysable activities',
  18: 'analysable activities',
  19: 'analysable activities',
  20: null,
  21: 'analysable activities',
  22: 'critical path activities',
  23: 'critical path activities',
  24: 'analysable activities',
  25: 'relationships',
  26: 'in-progress activities',
  27: 'relationships',
  28: 'analysable activities',
  29: 'WBS nodes',
  30: 'WBS nodes',
  31: 'analysable activities',
  32: 'analysable activities',
  33: 'analysable activities',
  34: 'analysable activities',
  35: 'analysable activities',
  36: 'analysable activities',
  37: 'analysable activities',
});

/**
 * High-impact category numbers per Amendment §S1. Used by cover stat-card
 * Critical/Warnings split (see src/engine/coverClassification.js).
 */
export const HIGH_IMPACT_CATEGORIES = Object.freeze(new Set([1, 3, 4, 8]));

/**
 * Per-check penalty factor for `range` direction scoring (Q8 lock).
 * Factor is points-per-percentage-distance from the nearest range bound.
 */
const RANGE_PENALTY_FACTOR = Object.freeze({
  21: 5,   // Critical-activity proportion (v3.17 §1) — gentle penalty; the
           // explicit max_fail: score 0 at 40% (20 pts above the 5–20 band);
           // the metric floor 0% (5 pts below) scores 75 — asymmetry accepted
           // because a near-empty longest path is #20's defect class, not #21's
  29: 50,  // WBS depth — steep penalty (depth excursion is structural)
});

const XLSX_PREFIX = '/api/checks';

/**
 * Compute status_threshold from direction + thresholds + ratio_pct + numerator.
 * Centralized so every check derives status the same way.
 */
export function deriveStatusThreshold({
  direction,
  ratio_pct,
  numerator,
  threshold_low,
  threshold_high,
  hidden = false,
}) {
  if (hidden) return STATUS_THRESHOLD.HIDDEN;
  if (direction === DIRECTION.REPORT_ONLY) return STATUS_THRESHOLD.REPORT_ONLY;

  if (direction === DIRECTION.BINARY) {
    return numerator === 0 ? STATUS_THRESHOLD.PASS : STATUS_THRESHOLD.EXCEEDED;
  }

  if (direction === DIRECTION.LOWER_IS_BETTER) {
    return ratio_pct <= threshold_low ? STATUS_THRESHOLD.PASS : STATUS_THRESHOLD.EXCEEDED;
  }

  if (direction === DIRECTION.HIGHER_IS_BETTER) {
    return ratio_pct >= threshold_low ? STATUS_THRESHOLD.PASS : STATUS_THRESHOLD.EXCEEDED;
  }

  if (direction === DIRECTION.RANGE) {
    return (ratio_pct >= threshold_low && ratio_pct <= threshold_high)
      ? STATUS_THRESHOLD.PASS
      : STATUS_THRESHOLD.EXCEEDED;
  }

  throw new Error(`deriveStatusThreshold: unknown direction "${direction}"`);
}

/**
 * Compute score 0-100 (or null) per Q8 direction-aware formulas.
 *
 * - lower_is_better: 100 if ratio_pct≤threshold_low, 0 if ≥max_fail_high,
 *   linear between (Spec v2 §10).
 * - higher_is_better: 100 × (ratio_pct − max_fail_low) / (threshold_low − max_fail_low),
 *   floored 0, capped 100.
 * - range: 100 if within [threshold_low, threshold_high]; otherwise
 *   100 − distance × penalty_factor (per-check, see RANGE_PENALTY_FACTOR).
 * - binary: 100 on PASS, 0 on EXCEEDED.
 * - report_only: null (excluded from category averages).
 * - hidden: null.
 */
export function calculateCanonicalScore({
  direction,
  ratio_pct,
  threshold_low,
  threshold_high,
  max_fail_low,
  max_fail_high,
  status_threshold,
  check_number,
  hidden = false,
}) {
  if (hidden) return null;
  if (direction === DIRECTION.REPORT_ONLY) return null;

  if (direction === DIRECTION.BINARY) {
    return status_threshold === STATUS_THRESHOLD.PASS ? 100 : 0;
  }

  if (direction === DIRECTION.LOWER_IS_BETTER) {
    if (ratio_pct <= threshold_low) return 100;
    if (max_fail_high == null) return ratio_pct > threshold_low ? 0 : 100;
    if (ratio_pct >= max_fail_high) return 0;
    return Math.round(100 * (1 - (ratio_pct - threshold_low) / (max_fail_high - threshold_low)));
  }

  if (direction === DIRECTION.HIGHER_IS_BETTER) {
    if (ratio_pct >= threshold_low) return 100;
    const floor = max_fail_low ?? 0;
    if (ratio_pct <= floor) return 0;
    return Math.max(0, Math.min(100, Math.round(100 * (ratio_pct - floor) / (threshold_low - floor))));
  }

  if (direction === DIRECTION.RANGE) {
    if (ratio_pct >= threshold_low && ratio_pct <= threshold_high) return 100;
    const factor = RANGE_PENALTY_FACTOR[check_number] ?? 5;
    const distance = ratio_pct < threshold_low ? threshold_low - ratio_pct : ratio_pct - threshold_high;
    return Math.max(0, Math.round(100 - distance * factor));
  }

  throw new Error(`calculateCanonicalScore: unknown direction "${direction}"`);
}

const STANDARD_KEYS = new Set([
  'check_number', 'check_name', 'category', 'category_number', 'finding_type',
  'numerator', 'denominator', 'ratio_pct',
  'threshold_low', 'threshold_high', 'threshold_label', 'direction',
  'status_threshold', 'status_severity',
  'severity_findings', 'affected_activities',
  'score', 'xlsx_url', 'hidden',
  'denominator_label', 'result_display_override',
]);

/**
 * Build a canonical CheckResult per Amendment §A1.
 *
 * Required: check_number, check_name, category, category_number, finding_type,
 * numerator, denominator. Everything else is derived or has a sensible default.
 *
 * `aux` is spread into the result as top-level properties (Q3). Aux keys must
 * not collide with §A1 standard keys — collision throws.
 */
export function createCanonicalCheckResult({
  check_number,
  check_name,
  category,
  category_number,
  finding_type,
  numerator,
  denominator,
  ratio_pct,
  threshold_low = null,
  threshold_high = null,
  max_fail_low = null,
  max_fail_high = null,
  threshold_label,
  denominator_label,
  result_display_override = null,
  direction,
  status_threshold: statusThresholdOverride,
  status_severity = STATUS_SEVERITY.NONE,
  severity_findings = [],
  affected_activities = [],
  hidden = false,
  score: scoreOverride,
  xlsx_url,
  aux = {},
}) {
  if (check_number == null || !THRESHOLD_LABELS[check_number]) {
    throw new Error(`createCanonicalCheckResult: unknown check_number "${check_number}"`);
  }

  const labelEntry = THRESHOLD_LABELS[check_number];
  const finalDirection = direction ?? labelEntry.direction;
  const finalLabel = threshold_label ?? labelEntry.label;
  const finalDenomLabel = denominator_label !== undefined
    ? denominator_label
    : (DENOMINATOR_LABELS[check_number] ?? null);
  const finalXlsx = xlsx_url ?? `${XLSX_PREFIX}/${check_number}.xlsx`;

  let finalRatio = ratio_pct;
  if (finalRatio === undefined) {
    if (finalDirection === DIRECTION.REPORT_ONLY || finalDirection === DIRECTION.BINARY) {
      finalRatio = null;
    } else {
      finalRatio = denominator > 0 ? (numerator / denominator) * 100 : 0;
    }
  }

  const status_threshold = statusThresholdOverride ?? deriveStatusThreshold({
    direction: finalDirection,
    ratio_pct: finalRatio,
    numerator,
    threshold_low,
    threshold_high,
    hidden,
  });

  const score = scoreOverride !== undefined ? scoreOverride : calculateCanonicalScore({
    direction: finalDirection,
    ratio_pct: finalRatio,
    threshold_low,
    threshold_high,
    max_fail_low,
    max_fail_high,
    status_threshold,
    check_number,
    hidden,
  });

  for (const k of Object.keys(aux)) {
    if (STANDARD_KEYS.has(k)) {
      throw new Error(`createCanonicalCheckResult: aux key "${k}" collides with standard §A1 field`);
    }
  }

  return {
    check_number,
    check_name,
    category,
    category_number,
    finding_type,
    numerator,
    denominator,
    ratio_pct: finalRatio,
    threshold_low,
    threshold_high,
    threshold_label: finalLabel,
    denominator_label: finalDenomLabel,
    result_display_override,
    direction: finalDirection,
    status_threshold,
    status_severity,
    severity_findings,
    affected_activities,
    score,
    xlsx_url: finalXlsx,
    hidden,
    ...aux,
  };
}

/**
 * Runtime conformance guard. Throws on any §A1 violation — used by
 * scripts/smoke-canonical-shape.mjs and selectively in engine consumers.
 */
export function assertCanonicalShape(r) {
  if (!r || typeof r !== 'object') throw new Error('assertCanonicalShape: not an object');

  const ctx = `check #${r.check_number ?? '?'}`;
  const must = (cond, msg) => { if (!cond) throw new Error(`${ctx}: ${msg}`); };

  must(typeof r.check_number === 'number' && r.check_number >= 1 && r.check_number <= 37,
    `check_number must be 1-37, got ${r.check_number}`);
  must(typeof r.check_name === 'string' && r.check_name.length > 0, 'check_name missing');
  must(typeof r.category === 'string' && r.category.length > 0, 'category missing');
  must(typeof r.category_number === 'number', 'category_number missing');
  must(Object.values(FINDING_TYPE).includes(r.finding_type), `finding_type invalid: ${r.finding_type}`);

  must(typeof r.numerator === 'number' && Number.isFinite(r.numerator), 'numerator must be a finite number');
  must(typeof r.denominator === 'number' && Number.isFinite(r.denominator), 'denominator must be a finite number');

  must(Object.values(DIRECTION).includes(r.direction), `direction invalid: ${r.direction}`);
  must(typeof r.threshold_label === 'string' && r.threshold_label.length > 0, 'threshold_label missing');

  const expected = THRESHOLD_LABELS[r.check_number];
  // CERT-FIX-1B (v3.17 §1): #21 flips to a report-only guarded state below 100
  // analysable activities — accept its informational label + report_only
  // direction as a valid shape alongside the scored range state.
  const GUARDED_21_LABEL = 'Critical activities: informational below 100 activities';
  const isGuarded21 = r.check_number === 21 && r.direction === DIRECTION.REPORT_ONLY;
  if (isGuarded21) {
    must(r.threshold_label === GUARDED_21_LABEL,
      `#21 guarded label drift — expected "${GUARDED_21_LABEL}" got "${r.threshold_label}"`);
  } else {
    must(r.threshold_label === expected.label,
      `threshold_label drift — expected "${expected.label}" got "${r.threshold_label}"`);
    must(r.direction === expected.direction,
      `direction drift — expected "${expected.direction}" got "${r.direction}"`);
  }

  // PDF-POLISH-2 D22: every non-binary check must emit a denominator_label
  // (short noun phrase appended to "N of M …"). Binary checks (#20) must use
  // result_display_override instead.
  if (r.direction === DIRECTION.BINARY) {
    must(typeof r.result_display_override === 'object' && r.result_display_override !== null,
      `binary check must emit result_display_override {pass, fail}, got ${r.result_display_override}`);
    must(typeof r.result_display_override.pass === 'string' && r.result_display_override.pass.length > 0,
      'result_display_override.pass missing');
    must(typeof r.result_display_override.fail === 'string' && r.result_display_override.fail.length > 0,
      'result_display_override.fail missing');
  } else {
    must(typeof r.denominator_label === 'string' && r.denominator_label.length > 0,
      `denominator_label missing or empty for non-binary check, got ${JSON.stringify(r.denominator_label)}`);
  }

  must(Object.values(STATUS_THRESHOLD).includes(r.status_threshold),
    `status_threshold invalid: ${r.status_threshold}`);
  must(Object.values(STATUS_SEVERITY).includes(r.status_severity),
    `status_severity invalid: ${r.status_severity}`);

  must(Array.isArray(r.severity_findings), 'severity_findings must be an array');
  must(Array.isArray(r.affected_activities), 'affected_activities must be an array');

  must(typeof r.xlsx_url === 'string' && r.xlsx_url.startsWith('/api/checks/'),
    `xlsx_url malformed: ${r.xlsx_url}`);

  if (r.direction === DIRECTION.REPORT_ONLY) {
    // CERT-FIX-1B (v3.17 §1): the guarded #21 is report-only but a SCHEDULE-LEVEL
    // proportion — it deliberately keeps ratio_pct (the proportion is emitted for
    // display, unlike the report-COUNT checks #5/#14/#17/#25/#27 which carry no
    // meaningful ratio). Score still null; ratio_pct must be a finite number.
    if (r.check_number === 21) {
      must(typeof r.ratio_pct === 'number' && Number.isFinite(r.ratio_pct),
        `#21 guarded report_only must keep a finite ratio_pct, got ${r.ratio_pct}`);
    } else {
      must(r.ratio_pct === null, `report_only must have ratio_pct=null, got ${r.ratio_pct}`);
    }
    must(r.score === null, `report_only must have score=null, got ${r.score}`);
    must(r.status_threshold === STATUS_THRESHOLD.REPORT_ONLY || r.status_threshold === STATUS_THRESHOLD.HIDDEN,
      `report_only must have status_threshold=REPORT_ONLY|HIDDEN, got ${r.status_threshold}`);
  } else if (r.status_threshold === STATUS_THRESHOLD.HIDDEN) {
    must(r.score === null, `HIDDEN must have score=null, got ${r.score}`);
  } else if (r.direction === DIRECTION.BINARY) {
    must(r.score === 0 || r.score === 100, `binary score must be 0|100, got ${r.score}`);
  } else {
    must(typeof r.ratio_pct === 'number' && Number.isFinite(r.ratio_pct),
      `ratio_pct must be a finite number, got ${r.ratio_pct}`);
    must(typeof r.score === 'number' && r.score >= 0 && r.score <= 100,
      `score must be 0-100, got ${r.score}`);
  }

  // Activity-level numerator/affected_activities relationship is direction-
  // aware (sub1-fix). lower_is_better: numerator IS the bad count, so it
  // equals affected_activities.length. higher_is_better: numerator is the
  // GOOD count; affected_activities is the BAD list, so they sum to the
  // denominator. range/binary/report_only: no invariant (numerator semantics
  // are check-specific — e.g., #21 ratio of CP days, #20 binary 0|1).
  if (r.finding_type === FINDING_TYPE.ACTIVITY_LEVEL) {
    if (r.direction === DIRECTION.LOWER_IS_BETTER) {
      must(r.numerator === r.affected_activities.length,
        `activity_level/lower_is_better: numerator (${r.numerator}) must equal affected_activities.length (${r.affected_activities.length})`);
    } else if (r.direction === DIRECTION.HIGHER_IS_BETTER) {
      must(r.numerator + r.affected_activities.length === r.denominator,
        `activity_level/higher_is_better: numerator (${r.numerator}) + affected_activities.length (${r.affected_activities.length}) must equal denominator (${r.denominator})`);
    }
  }
}
